import { useEffect, useState, type FormEvent } from "react";
import { Building2, UserPlus, Copy, UserMinus } from "lucide-react";
import { apiFetch } from "../api/client";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useAuth } from "../contexts/AuthContext";

type Member = {
  workspace_id: string;
  user_id: string;
  role: "admin" | "member";
  email: string;
};

export function Workspace() {
  const { user } = useAuth();
  const { activeWorkspace, setWorkspaces, setActiveWorkspace } = useWorkspace();
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  const [removeError, setRemoveError] = useState("");

  const myMembership = members.find((m) => m.user_id === user?.id);
  const isAdmin = myMembership?.role === "admin";

  async function loadMembers() {
    if (!activeWorkspace) return;
    setMembersLoading(true);
    const res = await apiFetch(`/workspaces/${activeWorkspace.id}/members`);
    if (res.ok) setMembers(await res.json());
    setMembersLoading(false);
  }

  useEffect(() => {
    setInviteLink("");
    setInviteMessage("");
    setRemoveError("");
    setMembers([]);
    loadMembers();
  }, [activeWorkspace]);

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!activeWorkspace) return;
    setInviteMessage("");
    const res = await apiFetch(`/workspaces/${activeWorkspace.id}/invite`, {
      method: "POST",
      body: JSON.stringify({ email: inviteEmail }),
    });
    if (res.ok) {
      const data = await res.json();
      setInviteLink(data.join_url);
      setInviteEmail("");
      setInviteMessage("Invite created. Share the link below:");
    } else {
      const data = await res.json().catch(() => ({}));
      setInviteMessage((data as { detail?: string }).detail ?? "Invite failed");
    }
  }

  async function handleRemoveMember(memberId: string) {
    if (!activeWorkspace) return;
    if (!confirm("Remove this member from the workspace?")) return;
    setRemoveError("");
    const res = await apiFetch(`/workspaces/${activeWorkspace.id}/members/${memberId}`, {
      method: "DELETE",
    });
    if (res.ok || res.status === 204) {
      loadMembers();
    } else {
      const data = await res.json().catch(() => ({}));
      setRemoveError((data as { detail?: string }).detail ?? "Failed to remove member");
    }
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError("");
    setCreating(true);
    const res = await apiFetch("/workspaces", {
      method: "POST",
      body: JSON.stringify({ name: newWorkspaceName }),
    });
    if (res.ok) {
      const created = await res.json();
      const allRes = await apiFetch("/workspaces");
      if (allRes.ok) {
        const all = await allRes.json();
        setWorkspaces(all);
        setActiveWorkspace(created);
      }
      setNewWorkspaceName("");
    } else {
      const data = await res.json().catch(() => ({}));
      setCreateError((data as { detail?: string }).detail ?? "Failed to create workspace");
    }
    setCreating(false);
  }

  const inputCls = "flex-1 bg-field border border-white/[0.08] rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand transition-colors";

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
        <Building2 size={22} className="text-brand" />
        Workspace
      </h1>

      {!activeWorkspace ? (
        <div className="bg-card rounded-xl p-6 space-y-4 border border-white/[0.05]">
          <h2 className="text-lg font-semibold text-ink">Create a workspace</h2>
          <p className="text-ink-2 text-sm">
            Workspaces let you collaborate with teammates and share projects.
          </p>
          {createError && (
            <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2">{createError}</p>
          )}
          <form onSubmit={handleCreate} className="flex space-x-3">
            <input
              type="text"
              required
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder="Workspace name"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={creating}
              className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            >
              {creating ? "Creating…" : "Create"}
            </button>
          </form>
        </div>
      ) : (
        <>
          <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
            <h2 className="text-xl font-semibold text-ink">{activeWorkspace.name}</h2>
            <p className="text-ink-3 text-sm mt-1">
              {members.length} {members.length === 1 ? "member" : "members"} ·{" "}
              Your role: <span className="capitalize">{myMembership?.role ?? "…"}</span>
            </p>
          </div>

          <div className="bg-card rounded-xl p-6 border border-white/[0.05]">
            <h2 className="text-lg font-semibold text-ink mb-4">Members</h2>
            {removeError && (
              <p className="text-red-400 text-sm mb-3">{removeError}</p>
            )}
            {membersLoading ? (
              <p className="text-ink-2 text-sm">Loading…</p>
            ) : (
              <div className="divide-y divide-white/[0.06]">
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="py-3 flex items-center justify-between"
                  >
                    <div>
                      <p className="text-ink text-sm">{member.email}</p>
                      <p className="text-ink-3 text-xs capitalize mt-0.5">{member.role}</p>
                    </div>
                    {isAdmin && member.user_id !== user?.id && (
                      <button
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-400/60 hover:text-red-400 transition-colors"
                        title="Remove member"
                      >
                        <UserMinus size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="bg-card rounded-xl p-6 space-y-4 border border-white/[0.05]">
              <h2 className="text-lg font-semibold text-ink">Invite member</h2>
              {inviteMessage && (
                <p className="text-brand text-sm">{inviteMessage}</p>
              )}
              <form onSubmit={handleInvite} className="flex space-x-3">
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className={inputCls}
                />
                <button
                  type="submit"
                  className="bg-brand hover:bg-brand-2 text-white text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <UserPlus size={15} />
                  Invite
                </button>
              </form>
              {inviteLink && (
                <div className="bg-field rounded-lg p-3 border border-white/[0.06]">
                  <p className="text-ink-3 text-xs mb-1">Share this link:</p>
                  <p className="text-brand text-sm break-all font-mono">{inviteLink}</p>
                  <button
                    onClick={() => navigator.clipboard.writeText(inviteLink)}
                    className="text-ink-3 hover:text-ink-2 text-xs mt-2 transition-colors flex items-center gap-1"
                  >
                    <Copy size={11} />
                    Copy to clipboard
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
